// How a control's reference and title are shown on every standard page.
//
// A Control.id is an internal key: "ncsc-caf-A1.a", "soc2-CC6.1", "pci-req-12", "iso42001-6.1",
// "iasme-ca-micro-t3", "legal-jersey-breach". People read the standard's own reference instead
// ("A1.a", "CC6.1", "Req 12", "6.1", "Theme 3"). ISO 27001 ids are already the standard's
// references ("4.1", "A.5.1") and pass through unchanged.
//
// Several seeds also repeat the reference inside the name ("A1.a Board Direction",
// "Theme 3: Access Control", "A8.4 — Users restricted…"); displayControlTitle removes that, so a
// row never reads "A1.a  A1.a Board Direction". Pure: no React, no theme.
//
// Cyber Essentials requirement ids carry no reference ("fw_boundary", "mp_coverage"): it lives at
// the front of the title ("A4.1 — Boundary firewall…"). Both functions read the page's catalogue
// (data/cyberEssentialsControls.ts) for a CE id.

import { CONTROL_THEMES } from "../data/cyberEssentialsControls";

const RULES: Array<[RegExp, (m: RegExpMatchArray) => string]> = [
  // NCSC CAF contributing outcomes: ncsc-caf-A1.a -> A1.a
  [/^ncsc-caf-(.+)$/i, (m) => m[1]],
  // SOC 2 Trust Services Criteria: soc2-CC6.1 -> CC6.1
  [/^soc2-(.+)$/i, (m) => m[1]],
  // PCI DSS principal requirements: pci-req-12 -> Req 12
  [/^pci-req-(\d+)$/i, (m) => `Req ${m[1]}`],
  // IASME Cyber Assurance themes, one set per size tier: iasme-ca-micro-t3 -> Theme 3
  [/^iasme-ca-[a-z-]+-t(\d+)$/i, (m) => `Theme ${m[1]}`],
  // ISO management system standards other than 27001: iso9001-4.1 -> 4.1, iso42001-A.6.1.2 -> A.6.1.2
  [/^iso\d+-(.+)$/i, (m) => m[1]],
  // Legal obligations: legal-<law>-<duty> -> the duty in capitals ("legal-jersey-breach" -> "BREACH").
  // The Legal page keys its Jersey tier guidance on this form, so it must not change.
  [/^legal-[^-]+-(.+)$/i, (m) => m[1].toUpperCase().replace(/-/g, " ")],
];

/** Cyber Essentials requirement key -> its title, split into reference and the rest. */
const CE_REQUIREMENTS = new Map(
  CONTROL_THEMES.flatMap((t) => t.controls).map((c) => [c.key, splitLeadingRef(c.title)] as const),
);

/** The reference a person reads for a control id, without the platform's internal prefix. */
export function displayControlRef(controlId: string): string {
  const ce = CE_REQUIREMENTS.get(controlId);
  if (ce) return ce.reference ?? ce.title;
  for (const [re, fmt] of RULES) {
    const m = controlId.match(re);
    if (m) return fmt(m);
  }
  return controlId;
}

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * The title to show beside a reference: the control's own name, minus a leading copy of the
 * reference ("A1.a Board Direction" -> "Board Direction", "Theme 3: Access Control" ->
 * "Access Control"). Returns the name unchanged when it does not start with the reference.
 */
export function displayControlTitle(controlId: string, name: string, reference = displayControlRef(controlId)): string {
  const ce = CE_REQUIREMENTS.get(controlId);
  if (ce?.reference) return ce.title;
  const trimmed = name.trim();
  if (!reference) return trimmed;
  const re = new RegExp(`^${escapeRe(reference)}(?![\\w.])\\s*[:\\u2014\\u2013-]?\\s*`, "i");
  const stripped = trimmed.replace(re, "");
  return stripped.length > 0 ? stripped : trimmed;
}

/**
 * A reference written at the front of a title, for catalogues that carry no separate reference
 * (the Cyber Essentials requirements: "A8.4 — Users restricted from installing…").
 * Returns the reference and the rest of the title, or a null reference when there is none.
 */
export function splitLeadingRef(title: string): { reference: string | null; title: string } {
  // "A4.1 — Boundary firewall…", "A4.10/11 — Remote admin…" and "A6.1 [AUTO-FAIL] — All operating
  // systems…": the reference may cover two numbers, and a flag between it and the dash stays with
  // the title so it is still seen.
  const m = title.match(/^([A-Z]{0,3}\d+(?:\.\d+)*[a-z]?(?:\/\d+)?)\s*(\[[^\]]+\])?\s+[—–-]\s+(.+)$/);
  return m ? { reference: m[1], title: m[2] ? `${m[2]} ${m[3]}` : m[3] } : { reference: null, title };
}

const STANDARD_PREFIXES: Array<[RegExp, (m: RegExpMatchArray) => string]> = [
  [/^iso(\d+)-/i, (m) => `ISO ${m[1]}`],
  [/^soc2-/i, () => "SOC 2"],
  [/^ncsc-caf-/i, () => "CAF"],
  [/^pci-/i, () => "PCI DSS"],
  [/^iasme-ca-/i, () => "IASME"],
];

/**
 * A control named outside its own standard's page, where the bare reference is ambiguous ("8.1" is
 * in ISO 27001 and ISO 45001): "ISO 45001 8.1", "SOC 2 CC6.1", "ISO 27001 A.5.1", "Cyber Essentials A4.1".
 * A legal obligation id keeps no readable reference, so it returns null and the caller counts it instead.
 */
export function controlRefWithStandard(controlId: string): string | null {
  if (/^legal-/i.test(controlId)) return null;
  if (CE_REQUIREMENTS.has(controlId)) return `Cyber Essentials ${displayControlRef(controlId)}`;
  for (const [re, std] of STANDARD_PREFIXES) {
    const m = controlId.match(re);
    if (m) return `${std(m)} ${displayControlRef(controlId)}`;
  }
  // ISO 27001's ids are its own references ("4.1", "A.5.1").
  if (/^(A\.)?\d/.test(controlId)) return `ISO 27001 ${controlId}`;
  return displayControlRef(controlId);
}

/** A list of control ids for a sentence: the readable ones, then "N legal obligations". */
export function describeControlIds(ids: string[], max = 10): string {
  const named = ids.map(controlRefWithStandard).filter((x): x is string => !!x);
  const legal = ids.length - named.length;
  const shown = named.slice(0, max);
  const parts = [...shown];
  if (named.length > max) parts.push(`${named.length - max} more`);
  if (legal > 0) parts.push(`${legal} legal obligation${legal === 1 ? "" : "s"}`);
  return parts.join(", ");
}
